import { Skeleton, Stack, Typography } from "@mui/material";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { BankAccountDetailPageContent } from "Components/BankAccounts/BankAccountDetail/BankAccountDetailPageContent";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { Link } from "Navigation/Link";
import { Resources, useResource } from "Translations/Resources";
import { appUrl } from "Utils/UrlUtils";
import { useParams } from "react-router-dom";
import type { ReactNode } from "react";

const PageResources = Resources.Pages.Content.BankAccounts.Detail;

export const BankAccountDetailPage = () => {
  const { publicID } = useParams<{ publicID: string }>();
  const { data, isLoading } = useBankingAccounts();
  const { t } = useResource();

  if (isLoading) {
    return (
      <Layout>
        <Stack gap={3}>
          <Skeleton variant="rounded" height="240px" />
          <Skeleton variant="rounded" height="280px" />
          <Skeleton variant="rounded" height="72px" />
        </Stack>
      </Layout>
    );
  }

  const account = data?.items.find((item) => item.publicID === publicID);

  if (!account) {
    return (
      <Layout>
        <PrimaryPaper>
          <Typography>{t(PageResources.AccountNotFound)}</Typography>
          <Typography
            component={Link}
            to={appUrl("bank-address-list")}
            sx={{ alignSelf: "flex-start" }}
          >
            {t(PageResources.BackToList)}
          </Typography>
        </PrimaryPaper>
      </Layout>
    );
  }

  return (
    <Layout>
      <BankAccountDetailPageContent account={account} />
    </Layout>
  );
};

type LayoutProps = {
  children: ReactNode;
};

const Layout = ({ children }: LayoutProps) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(Resources.Pages.Titles.BankAccountDetail)}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: appUrl("bank-address-list"),
      }}
    >
      <StyledPageWrapper>
        <div className="left-content">{children}</div>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};
